"use client";

import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input } from "antd";
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  CheckOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { useRegisterForm } from "../../hooks/useRegisterForm";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";
import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./RegisterForm1.css";

// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";
// layout d'origine de cette page : hero à gauche (classe signup-doctor)
const DEFAULT_HERO_ON_LEFT = true;

const FieldError = ({ msg }) =>
  msg ? (
    <span className="sp-field-error" role="alert">
      <ExclamationCircleFilled className="sp-field-error__icon" />
      {msg}
    </span>
  ) : null;

/* ─── Main component ─── */
export default function RegisterForm1() {
  const { form, errors, handleChange } = useRegisterForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? DEFAULT_HERO_ON_LEFT : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour le swap des 2 cards (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsPatient = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/SignupPatient", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  const password = form.password || "";
  const passwordRules = {
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[@$!%*?&]/.test(password),
  };

  const isPage1Valid = ["fullName", "email", "phone", "password", "confirmPassword"]
    .every((field) => form[field] && errors[field] === "");

  return (
    <div className="signup-container signup-doctor-auth">
      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form">
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("signup.createAccountTitlePrefix", "Create Your")} {" "}
                <span className="signup-form-title-accent">
                  {t("signup.createAccountTitleAccentDoctor", "Doctor Account")}
                </span>
              </h1>
            </div>

            <div className="signup-form-content">
              <div className="rf1-steps" aria-label="Progress indicator">
                <span className="rf1-step rf1-step--active" />
                <span className="rf1-step" />
                <span className="rf1-step" />
                <span className="rf1-step" />
              </div>
              <p className="rf1-step-label">
                {t("signup.stepLabel1", "Step 1 / 4")}
              </p>

              <div className="rf1-section-description">
                <h2 className="rf1-description-title">
                  {t("signup.doctor.step1SectionTitle", "PERSONAL INFORMATION")}
                </h2>
                <p className="rf1-description-text">
                  {t("signup.doctor.step1SectionDescription", "Join Our Network Of Trusted Medical Professionals. Please Create Your Account To Get Started.")}
                </p>
              </div>

              <div className="signup-form-group">
                <label className="signup-form-label">
                  {t("signup.doctor.fullNameLabel")} <span className="signup-required">*</span>
                </label>
                <Input
                  placeholder={t("signup.doctor.fullNamePlaceholder")}
                  value={form.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  className="signup-form-input"
                  prefix={<UserOutlined />}
                  status={errors.fullName ? "error" : ""}
                  aria-invalid={!!errors.fullName}
                />
                <FieldError msg={errors.fullName} />
              </div>

              <div className="signup-form-row">
                <div className="signup-form-group signup-half-width">
                  <label className="signup-form-label">
                    {t("commonn.email")} <span className="signup-required">*</span>
                  </label>
                  <Input
                    type="email"
                    placeholder={t("commonn.emailPlaceholder")}
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="signup-form-input"
                    prefix={<MailOutlined />}
                    status={errors.email ? "error" : ""}
                    aria-invalid={!!errors.email}
                  />
                  <FieldError msg={errors.email} />
                </div>

                <div className="signup-form-group signup-half-width">
                  <label className="signup-form-label">
                    {t("commonn.phone")} <span className="signup-required">*</span>
                  </label>
                  <PhoneInput
                    country="tn"
                    enableSearch
                    value={form.phone}
                    onChange={(phone) => handleChange("phone", phone)}
                    inputClass={`signup-phone-input${errors.phone ? " sp-phone--error" : ""}`}
                    containerClass="phone-input-container"
                    buttonClass="phone-input-flag"
                    specialLabel=""
                  />
                  <FieldError msg={errors.phone} />
                </div>
              </div>

              <div className="signup-form-group">
                <label className="signup-form-label">
                  {t("commonn.password")} <span className="signup-required">*</span>
                </label>
                <Input.Password
                  placeholder={t("commonn.passwordPlaceholder")}
                  value={form.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  className="signup-form-input"
                  prefix={<LockOutlined />}
                  status={errors.password ? "error" : ""}
                  aria-invalid={!!errors.password}
                />
                <FieldError msg={errors.password} />
            
              </div>

              {password && !Object.values(passwordRules).every(rule => rule) && (
                <ul className="signup-password-rules">
                  {[
                    { key: "minLength", label: t("signup.passwordRuleMinLength", "At least 8 characters") },
                    { key: "uppercase", label: t("signup.passwordRuleUppercase", "Uppercase letter") },
                    { key: "lowercase", label: t("signup.passwordRuleLowercase", "Lowercase letter") },
                    { key: "number", label: t("signup.passwordRuleNumber", "Number") },
                    { key: "special", label: t("signup.passwordRuleSpecial", "Special character (@$%!*&?)") },
                  ].map(({ key, label }) => (
                    <li
                      key={key}
                      className={`signup-password-rule ${passwordRules[key] ? "signup-password-rule-valid" : "signup-password-rule-invalid"}`}
                    >
                      {passwordRules[key] ? <CheckOutlined /> : <CloseOutlined />}
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="signup-form-group">
                <label className="signup-form-label">
                  {t("signup.doctor.confirmPasswordLabel")} <span className="signup-required">*</span>
                </label>
                <Input.Password
                  placeholder={t("signup.doctor.confirmPasswordPlaceholder")}
                  value={form.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  className="signup-form-input"
                  prefix={<LockOutlined />}
                  status={errors.confirmPassword ? "error" : ""}
                  aria-invalid={!!errors.confirmPassword}
                />
                <FieldError msg={errors.confirmPassword} />
              
              </div>

              <button
                className="signup-create-account-btn"
                disabled={!isPage1Valid}
                onClick={() => navigate("/register2")}
                type="button"
              >
                {t("commonn.continue", "Continue")}
              </button>
              <div className="signup-login-link-wrapper">
                <span className="signup-login-text">
                  {t("signup.alreadyHaveAccount", "Already have an account?")} {" "}
                  <span className="signup-login-link" onClick={() => navigate("/LoginDoctor")}>
                    {t("signup.signInNow", "Sign in now!")}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </section>

        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />
          <button
            type="button"
            className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
            onClick={() => navigate("/")}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>
          <div className="signup-hero-top-icon">
            <img src={icon1} alt="" />
          </div>

       

          <div className="signup-hero-inner">
            <h1 className="signup-hero-title">
              {t("signup.createAccountHeroTitle", "Are you signing up as a patient? This registration is for doctors only.")}
            </h1>
            <p className="signup-hero-sub">
              {t("signup.createAccountHeroText", "To book appointments and access care, please create a patient account instead.")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsPatient}
            >
              <span>{t("signup.continueAsPatient", "Continue as a patient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
